// src/utils/apiConnector.ts
import AppSearchAPIConnector from '@elastic/search-ui-app-search-connector';
import { getCurrentTenant } from '../config/tenants';

// ---------------------------------------------------------------------------
// Engine name resolution — per tenant, per historic mode
// ---------------------------------------------------------------------------
//
// Env var convention:
//
//   REACT_APP_SEARCH_KEY          shared API key (or per-tenant if you prefer)
//   REACT_APP_ENDPOINT_BASE       shared endpoint
//
//   REACT_APP_ENGINE_NAME                    porsche current
//   REACT_APP_ENGINE_NAME_HISTORIC           porsche historic
//   REACT_APP_ENGINE_NAME_MERCEDES           mercedes current
//   REACT_APP_ENGINE_NAME_MERCEDES_HISTORIC  mercedes historic
//
// If Mercedes-specific vars are not set, falls back to the Porsche ones so
// existing deployments don't break.
// ---------------------------------------------------------------------------

const resolveEngineName = (tenantId: string, historic: boolean): string => {
  if (tenantId === 'mercedes') {
    if (historic) {
      return (
        process.env.REACT_APP_ENGINE_NAME_MERCEDES_HISTORIC ||
        process.env.REACT_APP_ENGINE_NAME_HISTORIC ||
        'oldtimer-search-engine-historic'
      );
    }
    return (
      process.env.REACT_APP_ENGINE_NAME_MERCEDES ||
      process.env.REACT_APP_ENGINE_NAME ||
      'oldtimer-search-engine'
    );
  }

  // porsche (and any future tenant that hasn't been configured yet)
  if (historic) {
    return (
      process.env.REACT_APP_ENGINE_NAME_HISTORIC ||
      'oldtimer-search-engine-historic'
    );
  }
  return process.env.REACT_APP_ENGINE_NAME || 'oldtimer-search-engine';
};

// ---------------------------------------------------------------------------
// Historic mode helpers (unchanged from original)
// ---------------------------------------------------------------------------

export const isHistoricMode = (): boolean => {
  return sessionStorage.getItem('historicMode') === 'true';
};

export const setHistoricMode = (enabled: boolean): void => {
  sessionStorage.setItem('historicMode', enabled.toString());
};

// ---------------------------------------------------------------------------
// Main connector factory
// ---------------------------------------------------------------------------

export const createAppSearchConnector = () => {
  const searchKey   = process.env.REACT_APP_SEARCH_KEY;
  const endpointBase = process.env.REACT_APP_ENDPOINT_BASE;

  // Resolve historic mode (sessionStorage first, then URL hash for back-compat)
  let historic = sessionStorage.getItem('historicMode') === 'true';
  if (!historic) {
    const hash = window.location.hash.substring(1);
    const hashParams = new URLSearchParams(hash);
    const HISTORY_KEY =
      process.env.REACT_APP_HISTORIC_ACCESS_KEY || 'test-historic-key';
    historic = hashParams.get('all-history') === HISTORY_KEY;
    if (historic) {
      sessionStorage.setItem('historicMode', 'true');
    }
  }

  // Resolve tenant
  const tenant = getCurrentTenant();

  const engineName = resolveEngineName(tenant.id, historic);

  console.log(
    `🚀 Tenant: ${tenant.id} | Engine: ${engineName} | Mode: ${historic ? 'HISTORIC' : 'CURRENT'}`
  );

  if (!searchKey || !engineName || !endpointBase) {
    console.error('Missing environment variables for App Search API Connector');
    return null;
  }

  return new AppSearchAPIConnector({ searchKey, engineName, endpointBase });
};